import { IDENTITY_MATRIX, type Matrix } from '../core/geometry';
import { createId, createLayerBase, type TextLayer, type VectorLayer } from '../core/model';
import { hueShiftHex } from '../tooling/pixel-ops';
import type { EditableText, EllipseObject, PathNode, PathObject, RectangleObject, SolidPaint, VectorObject, VectorScene, VectorStroke } from './types';
import { effectivePathGeometry } from './path';

function solid(color: string) {
  return { type: 'solid' as const, color, alpha: 1 };
}

function objectBase(name: string, fill: string): Omit<RectangleObject, 'type' | 'geometry'> {
  return {
    id: createId('vector'),
    name,
    visible: true,
    opacity: 1,
    transform: IDENTITY_MATRIX,
    fill: solid(fill),
    stroke: { paint: solid('#163c2a'), width: 2, lineCap: 'round', lineJoin: 'round', dash: [] },
    extensions: {},
  };
}

export function createRectangleObject(): RectangleObject {
  return { ...objectBase('Rectangle', '#65c28b'), type: 'rectangle', geometry: { x: 0, y: 0, width: 320, height: 220, radius: { topLeft: 24, topRight: 24, bottomRight: 24, bottomLeft: 24 } } };
}

export function createEllipseObject(): EllipseObject {
  return { ...objectBase('Ellipse', '#d5a34e'), type: 'ellipse', geometry: { x: 0, y: 0, width: 260, height: 260 } };
}

export function createShapeLayer(kind: 'rectangle' | 'ellipse' = 'rectangle'): VectorLayer {
  const scene: VectorScene = { schemaVersion: 2, objects: [kind === 'rectangle' ? createRectangleObject() : createEllipseObject()], extensions: {} };
  return { ...createLayerBase(kind === 'rectangle' ? 'Rectangle' : 'Ellipse'), kind: 'vector', transform: [1, 0, 0, 1, 160, 140], vector: scene };
}

export function createPathLayer(inputNodes: readonly Omit<PathNode, 'id' | 'subpathId'>[], closed = false): VectorLayer {
  const layer = { ...createLayerBase('Path'), kind: 'vector' as const, vector: { schemaVersion: 2 as const, objects: [] as VectorObject[], extensions: {} } };
  const subpathId = createId('subpath');
  const nodes: PathNode[] = inputNodes.map((node) => ({ ...structuredClone(node), id: createId('node'), subpathId }));
  const base = objectBase('Path', '#65c28b');
  const path: PathObject = {
    ...base,
    type: 'path',
    fill: closed ? base.fill : null,
    geometry: { nodes, subpaths: [{ id: subpathId, nodeIds: nodes.map((node) => node.id), closed }], fillRule: 'nonzero' },
  };
  layer.vector.objects.push(path);
  return layer;
}

/** Style of a new browser-text layer (headless tooling measures against it before the layer exists). */
export const DEFAULT_TEXT_STYLE: Readonly<EditableText['style']> = Object.freeze({ family: 'Inter, sans-serif', weight: 600, size: 64, lineHeight: 1.2, tracking: 0, align: 'left', color: '#1f704a' });

export function createTextLayer(): TextLayer {
  const text: EditableText = {
    schemaVersion: 1,
    value: 'Editable text',
    box: { width: 520, height: 160, autoHeight: true },
    style: { ...DEFAULT_TEXT_STYLE },
    missingFont: false,
    extensions: {},
  };
  return { ...createLayerBase('Text'), kind: 'text', transform: [1, 0, 0, 1, 160, 180], text };
}

export function cloneVectorObject(object: VectorObject): VectorObject {
  return { ...structuredClone(object), id: createId('vector') };
}

export function recolorVectorObject(object: VectorObject, degrees: number): VectorObject {
  const fill = object.fill ? { ...object.fill, color: hueShiftHex(object.fill.color, degrees), swatchId: undefined } : null;
  const stroke = object.stroke ? { ...object.stroke, paint: { ...object.stroke.paint, color: hueShiftHex(object.stroke.paint.color, degrees), swatchId: undefined } } : null;
  return { ...object, fill, stroke } as VectorObject;
}

function parseHex(color: string): [number, number, number] {
  const match = /^#([\da-f]{6})$/i.exec(color);
  const value = match?.[1] ? Number.parseInt(match[1], 16) : 0;
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function mixColor(left: string, right: string, amount: number): string {
  const a = parseHex(left);
  const b = parseHex(right);
  return `#${a.map((channel, index) => Math.round(channel + ((b[index] ?? channel) - channel) * amount).toString(16).padStart(2, '0')).join('')}`;
}

const mix = (left: number, right: number, amount: number) => left + (right - left) * amount;

function mixPaint(left: SolidPaint, right: SolidPaint, amount: number): SolidPaint {
  return {
    type: 'solid',
    color: mixColor(left.color, right.color, amount),
    alpha: mix(left.alpha, right.alpha, amount),
    ...(left.tint === undefined ? {} : { tint: left.tint }),
  };
}

function cloneUnlinkedPaint(paint: SolidPaint | null): SolidPaint | null {
  if (!paint) return null;
  return {
    type: 'solid',
    color: paint.color,
    alpha: paint.alpha,
    ...(paint.tint === undefined ? {} : { tint: paint.tint }),
  };
}

function cloneUnlinkedStroke(stroke: VectorStroke | null): VectorStroke | null {
  return stroke ? { ...structuredClone(stroke), paint: cloneUnlinkedPaint(stroke.paint)! } : null;
}

export function blendVectorObjects(left: VectorObject, right: VectorObject, steps: number): VectorObject[] {
  if (left.type !== right.type || left.type === 'path' || right.type === 'path') return [];
  const count = Math.max(1, Math.min(100, Math.round(steps)));
  return Array.from({ length: count }, (_, index) => {
    const amount = (index + 1) / (count + 1);
    const object = cloneVectorObject(left);
    object.name = `Blend ${index + 1}`;
    object.opacity = mix(left.opacity, right.opacity, amount);
    object.transform = left.transform.map((value, component) => mix(value, right.transform[component] ?? value, amount)) as unknown as Matrix;
    object.fill = left.fill && right.fill ? mixPaint(left.fill, right.fill, amount) : cloneUnlinkedPaint(amount < 0.5 ? left.fill : right.fill);
    object.stroke = left.stroke && right.stroke ? {
      ...left.stroke,
      width: mix(left.stroke.width, right.stroke.width, amount),
      paint: mixPaint(left.stroke.paint, right.stroke.paint, amount),
    } : cloneUnlinkedStroke(amount < 0.5 ? left.stroke : right.stroke);
    if (left.type === 'rectangle' && object.type === 'rectangle' && right.type === 'rectangle') {
      object.geometry = {
        x: mix(left.geometry.x, right.geometry.x, amount),
        y: mix(left.geometry.y, right.geometry.y, amount),
        width: mix(left.geometry.width, right.geometry.width, amount),
        height: mix(left.geometry.height, right.geometry.height, amount),
        radius: {
          topLeft: mix(left.geometry.radius.topLeft, right.geometry.radius.topLeft, amount),
          topRight: mix(left.geometry.radius.topRight, right.geometry.radius.topRight, amount),
          bottomRight: mix(left.geometry.radius.bottomRight, right.geometry.radius.bottomRight, amount),
          bottomLeft: mix(left.geometry.radius.bottomLeft, right.geometry.radius.bottomLeft, amount),
        },
      };
    } else if (object.type === 'ellipse' && right.type === 'ellipse') {
      object.geometry = {
        x: mix(left.geometry.x, right.geometry.x, amount),
        y: mix(left.geometry.y, right.geometry.y, amount),
        width: mix(left.geometry.width, right.geometry.width, amount),
        height: mix(left.geometry.height, right.geometry.height, amount),
      };
    }
    return object;
  });
}

function pathOffsetDelta(
  previous: PathNode,
  node: PathNode,
  next: PathNode,
  distance: number,
  clockwise: boolean,
  endpoint: 'start' | 'end' | null,
): { x: number; y: number } {
  const normal = (from: PathNode, to: PathNode) => {
    const dx = to.anchor.x - from.anchor.x;
    const dy = to.anchor.y - from.anchor.y;
    const length = Math.hypot(dx, dy) || 1;
    return clockwise ? { x: dy / length, y: -dx / length } : { x: -dy / length, y: dx / length };
  };
  const incoming = normal(previous, node);
  const outgoing = normal(node, next);
  if (endpoint === 'start') return { x: outgoing.x * distance, y: outgoing.y * distance };
  if (endpoint === 'end') return { x: incoming.x * distance, y: incoming.y * distance };
  const sumX = incoming.x + outgoing.x;
  const sumY = incoming.y + outgoing.y;
  const sumLength = Math.hypot(sumX, sumY);
  if (sumLength < 0.0001) return { x: outgoing.x * distance, y: outgoing.y * distance };
  const unitX = sumX / sumLength;
  const unitY = sumY / sumLength;
  const denominator = Math.max(0.25, Math.abs((unitX * outgoing.x) + (unitY * outgoing.y)));
  const scale = distance / denominator;
  return { x: unitX * scale, y: unitY * scale };
}

export function offsetVectorObject(source: VectorObject, distance: number): VectorObject | null {
  if (!Number.isFinite(distance) || Math.abs(distance) < 0.01) return null;
  const object = cloneVectorObject(source);
  object.name = `${source.name} offset`;
  if (object.type === 'rectangle' && source.type === 'rectangle') {
    const width = source.geometry.width + distance * 2;
    const height = source.geometry.height + distance * 2;
    if (width <= 0 || height <= 0) return null;
    const limit = Math.min(width, height) / 2;
    object.geometry = {
      x: source.geometry.x - distance,
      y: source.geometry.y - distance,
      width,
      height,
      radius: {
        topLeft: Math.max(0, Math.min(limit, source.geometry.radius.topLeft + distance)),
        topRight: Math.max(0, Math.min(limit, source.geometry.radius.topRight + distance)),
        bottomRight: Math.max(0, Math.min(limit, source.geometry.radius.bottomRight + distance)),
        bottomLeft: Math.max(0, Math.min(limit, source.geometry.radius.bottomLeft + distance)),
      },
    };
    return object;
  }
  if (object.type === 'ellipse' && source.type === 'ellipse') {
    const width = source.geometry.width + distance * 2;
    const height = source.geometry.height + distance * 2;
    if (width <= 0 || height <= 0) return null;
    object.geometry = { x: source.geometry.x - distance, y: source.geometry.y - distance, width, height };
    return object;
  }
  if (object.type !== 'path' || source.type !== 'path') return null;
  const effectiveSource = effectivePathGeometry(source.geometry);
  object.geometry = structuredClone(effectiveSource);
  const sourceNodes = new Map(effectiveSource.nodes.map((node) => [node.id, node]));
  const deltas = new Map<string, { x: number; y: number }>();
  for (const subpath of effectiveSource.subpaths) {
    const nodes = subpath.nodeIds.flatMap((id) => {
      const node = sourceNodes.get(id);
      return node ? [node] : [];
    });
    if (nodes.length < 2) continue;
    const area = nodes.reduce((sum, node, index) => {
      const next = nodes[(index + 1) % nodes.length];
      return next ? sum + (node.anchor.x * next.anchor.y) - (next.anchor.x * node.anchor.y) : sum;
    }, 0);
    nodes.forEach((node, index) => {
      const previous = subpath.closed ? nodes[index - 1] ?? nodes.at(-1) ?? node : nodes[index - 1] ?? node;
      const next = subpath.closed ? nodes[index + 1] ?? nodes[0] ?? node : nodes[index + 1] ?? node;
      const endpoint = subpath.closed ? null : index === 0 ? 'start' : index === nodes.length - 1 ? 'end' : null;
      deltas.set(node.id, pathOffsetDelta(previous, node, next, distance, area >= 0, endpoint));
    });
  }
  object.geometry = {
    ...object.geometry,
    nodes: object.geometry.nodes.map((node) => {
      const delta = deltas.get(node.id);
      if (!delta) return node;
      const shift = (point: { x: number; y: number } | null) => point ? { x: point.x + delta.x, y: point.y + delta.y } : null;
      return { ...node, anchor: shift(node.anchor)!, incoming: shift(node.incoming), outgoing: shift(node.outgoing) };
    }),
  };
  return object;
}
